/**
 * The Tango palette.
 *
 * Ubuntu's default terminal profile, and the ramp GNOME Terminal has shipped
 * for years. It lives here rather than inside the terminal because two
 * surfaces now read from it: `terminal.ts`, where it is the ANSI ramp the
 * guest writes into, and `EditorPane.tsx`, where it is the syntax scheme.
 *
 * That shared source is the point. The work pane shows one machine behind
 * three tabs, and a file that is magenta in the editor is magenta when `cat`
 * prints it beside it. Two palettes would have made the pane read as two
 * products; one makes the colours mean the same thing on both sides of the
 * tab strip.
 *
 * The 16 below are Tango exactly — do not tune them. What a learner reads
 * here has to be what they will read on a real box, because the whole reason
 * for taking somebody else's palette is transfer.
 */
export const tango = {
  black: '#2e3436',
  red: '#cc0000',
  green: '#4e9a06',
  yellow: '#c4a000',
  blue: '#3465a4',
  magenta: '#75507b',
  cyan: '#06989a',
  white: '#d3d7cf',
  brightBlack: '#555753',
  brightRed: '#ef2929',
  brightGreen: '#8ae234',
  brightYellow: '#fce94f',
  brightBlue: '#729fcf',
  brightMagenta: '#ad7fa8',
  brightCyan: '#34e2e2',
  brightWhite: '#eeeeec',
} as const;

/**
 * Two greys off the same family (Tango's Aluminium 4 and a step below it)
 * that the 16-colour ramp does not carry.
 *
 * The editor needs them and the terminal does not: a terminal only ever
 * prints one of the sixteen, but code has two whole classes of token you are
 * meant to read *past* — comments and punctuation — and `brightBlack`
 * (#555753) is too dark to carry a full line of prose. These are that ramp,
 * and they are the one place the editor extends Tango rather than quoting it.
 */
export const tangoQuiet = {
  /** Comments: present, legible, never competing. ~6:1 on the floor black. */
  comment: '#888a85',
  /** Brackets, separators, the scaffolding you read past. ~4:1. */
  scaffold: '#6a6f6b',
} as const;
